import { useState, useMemo, useEffect } from 'react';
import { isPastDate } from '@wix/da-ds/pkg/Time/functions';
import { getAllowOffers, getHasBuyItNow, getIsPending, getHasOwnOffer, getHasRejectedOffer, getIsOnSale, getIsAdoptableOfferType, getCanSeeMinPrice, getCanSeeOfferCountdown, getCanSeeEarlyAccessCountdown, getOfferCountdownEndTime, getEarlyAccessCountdownEndTime } from '../../../../utils/adoptables';
import { SellableAs } from '../../../../types/deviation';
import { getSellableAs } from '../../../../types/deviation/getters';
export default function useAdoptableState(deviation) {
    var _deviation_extended_adoptable_offers, _deviation_extended_adoptable, _deviation_extended, _deviation_extended_adoptable1, _deviation_extended1, _deviation_adoptable_offers, _deviation_adoptable, _deviation_adoptable_fixedPrice, _deviation_adoptable1, _deviation_extended_adoptable2, _deviation_extended2, _deviation_extended3, _deviation_extended_adoptable3, _deviation_extended4;
    var _deviation_extended_adoptable_offers_offerList;
    const offersList = (_deviation_extended_adoptable_offers_offerList = (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : (_deviation_extended_adoptable_offers = _deviation_extended_adoptable.offers) === null || _deviation_extended_adoptable_offers === void 0 ? void 0 : _deviation_extended_adoptable_offers.offerList) !== null && _deviation_extended_adoptable_offers_offerList !== void 0 ? _deviation_extended_adoptable_offers_offerList : [];
    const hasOffers = offersList.length > 0;
    const hasOwnOffer = getHasOwnOffer(deviation);
    const isOfferType = getIsAdoptableOfferType(deviation);
    const hasRejectedOffer = getHasRejectedOffer(deviation);
    const hasBuyItNow = getHasBuyItNow(deviation);
    const isOnSale = getIsOnSale(deviation);
    // this is kind of hacky because the deviation entity is not actually updated when timer ends
    // however, I don't think we want to force a page reload in this case, or manually alter entities (do we?)
    const offerCountdownEndTime = getOfferCountdownEndTime(deviation);
    const [offerCountdownHasEnded, setOfferCountdownHasEnded] = useState(offerCountdownEndTime ? isPastDate(offerCountdownEndTime) : false);
    const onOfferTimerEnd = ()=>setOfferCountdownHasEnded(true);
    // this is kind of hacky because the deviation entity is not actually updated when timer ends
    // however, I don't think we want to force a page reload in this case, or manually alter entities (do we?)
    const earlyAccessCountdownEndTime = getEarlyAccessCountdownEndTime(deviation);
    const [earlyAccessCountdownHasEnded, setEarlyAccessCountdownHasEnded] = useState(earlyAccessCountdownEndTime ? isPastDate(earlyAccessCountdownEndTime) : false);
    const onEarlyAccessTimerEnd = ()=>setEarlyAccessCountdownHasEnded(true);
    const isPending = useMemo(()=>getIsPending(deviation), [
        deviation
    ]);
    const allowOffers = useMemo(()=>!isPending && getAllowOffers(deviation) && !offerCountdownHasEnded, [
        isPending,
        deviation,
        offerCountdownHasEnded
    ]);
    var _deviation_extended_adoptable_saleCount;
    const saleCount = (_deviation_extended_adoptable_saleCount = (_deviation_extended1 = deviation.extended) === null || _deviation_extended1 === void 0 ? void 0 : (_deviation_extended_adoptable1 = _deviation_extended1.adoptable) === null || _deviation_extended_adoptable1 === void 0 ? void 0 : _deviation_extended_adoptable1.saleCount) !== null && _deviation_extended_adoptable_saleCount !== void 0 ? _deviation_extended_adoptable_saleCount : 0;
    const minOfferPrice = allowOffers && ((_deviation_adoptable = deviation.adoptable) === null || _deviation_adoptable === void 0 ? void 0 : (_deviation_adoptable_offers = _deviation_adoptable.offers) === null || _deviation_adoptable_offers === void 0 ? void 0 : _deviation_adoptable_offers.offerPrice) || 0;
    const fixedPrice = hasBuyItNow && ((_deviation_adoptable1 = deviation.adoptable) === null || _deviation_adoptable1 === void 0 ? void 0 : (_deviation_adoptable_fixedPrice = _deviation_adoptable1.fixedPrice) === null || _deviation_adoptable_fixedPrice === void 0 ? void 0 : _deviation_adoptable_fixedPrice.fixedPrice) || 0;
    // Seller flags
    const isSeller = (_deviation_extended2 = deviation.extended) === null || _deviation_extended2 === void 0 ? void 0 : (_deviation_extended_adoptable2 = _deviation_extended2.adoptable) === null || _deviation_extended_adoptable2 === void 0 ? void 0 : _deviation_extended_adoptable2.isOwner;
    const canSellerViewOffers = isSeller && (isPending && hasOffers || isOnSale && (hasOffers || isOfferType));
    const isResellOptionEnabled = getSellableAs(deviation).includes(SellableAs.AdoptableResale);
    // Buyer flags
    const adoptable = (_deviation_extended3 = deviation.extended) === null || _deviation_extended3 === void 0 ? void 0 : _deviation_extended3.adoptable;
    const canAdopt = adoptable === null || adoptable === void 0 ? void 0 : adoptable.canAdopt;
    const isGiftable = adoptable === null || adoptable === void 0 ? void 0 : adoptable.isGiftable;
    const isBuyer = !isSeller;
    const canBuyNow = isBuyer && hasBuyItNow && canAdopt;
    const canMakeNewOffer = isBuyer && allowOffers && !hasOwnOffer && canAdopt;
    const canManageExistingOffer = isBuyer && allowOffers && hasOwnOffer && !hasRejectedOffer;
    const canBuyerViewOffers = isBuyer && isPending;
    // Other flags
    const canSeeMinOfferPrice = getCanSeeMinPrice(deviation);
    const canSeeOfferCountdown = getCanSeeOfferCountdown(deviation);
    const canSeeEarlyAccessCountdown = getCanSeeEarlyAccessCountdown(deviation);
    var _deviation_extended_adoptable_earlyAccess;
    const earlyAccess = (_deviation_extended_adoptable_earlyAccess = (_deviation_extended4 = deviation.extended) === null || _deviation_extended4 === void 0 ? void 0 : (_deviation_extended_adoptable3 = _deviation_extended4.adoptable) === null || _deviation_extended_adoptable3 === void 0 ? void 0 : _deviation_extended_adoptable3.earlyAccess) !== null && _deviation_extended_adoptable_earlyAccess !== void 0 ? _deviation_extended_adoptable_earlyAccess : {
        isInEarlyAccess: false
    };
    useEffect(()=>{
        setOfferCountdownHasEnded(offerCountdownEndTime ? isPastDate(offerCountdownEndTime) : false);
    // We update the value of `offerCountdownHasEnded` when `deviation` prop changes, to avoid having a stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        deviation
    ]);
    useEffect(()=>{
        setEarlyAccessCountdownHasEnded(earlyAccessCountdownEndTime ? isPastDate(earlyAccessCountdownEndTime) : false);
    // We update the value of `earlyAccessCountdownHasEnded` when `deviation` prop changes, to avoid having a stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        deviation
    ]);
    return {
        isSeller,
        isGiftable,
        isOfferType,
        canAdopt,
        canBuyNow,
        canMakeNewOffer,
        canManageExistingOffer,
        canBuyerViewOffers,
        canSellerViewOffers,
        canSeeMinOfferPrice,
        canSeeEarlyAccessCountdown,
        canSeeOfferCountdown,
        hasOffers,
        offersList,
        saleCount,
        isResellOptionEnabled,
        minOfferPrice,
        fixedPrice,
        offerCountdownEndTime,
        offerCountdownHasEnded,
        onOfferTimerEnd,
        earlyAccessCountdownEndTime,
        earlyAccessCountdownHasEnded,
        onEarlyAccessTimerEnd,
        earlyAccess
    };
}

//# sourceMappingURL=useAdoptableState.js.map